import { postJSON } from 'common/services/api'
import { useMutation, useQueryClient } from 'react-query'
import {
  INSTANCES_DELETE_QUERY_KEY,
  INSTANCES_DELETE_URL,
  REVISIONS_QUERY_KEY
} from './queryConstants'

type UseInstancesDeleteReturn = {
  deleteEvents?: (values: DeleteEventsPayload) => Promise<unknown>
  isLoadingDeleteEvents?: boolean
}

type DeleteEventsPayload = {
  patternId?: string
  updateOption: string
}

export type DeleteEventsResponse = {
  instances: unknown[]
  removedCounts: { published: number; draft: number }
}

export const isDeleteEventsResponse = (
  value: unknown
): value is DeleteEventsResponse => {
  if (
    typeof value !== 'object' ||
    value === null ||
    !('removedCounts' in value)
  ) {
    return false
  }

  const { removedCounts } = value as { removedCounts: unknown }

  return (
    typeof removedCounts === 'object' &&
    removedCounts !== null &&
    typeof (removedCounts as { published: unknown }).published === 'number' &&
    typeof (removedCounts as { draft: unknown }).draft === 'number'
  )
}

const useInstancesDelete = (): UseInstancesDeleteReturn => {
  const queryClient = useQueryClient()

  // Delete events

  const deleteEventsFunction = async (
    values: DeleteEventsPayload
  ): Promise<DeleteEventsResponse> => {
    return postJSON({
      url: `${INSTANCES_DELETE_URL}`,
      method: 'POST',
      json: values,
      isRota: true
    })
  }

  const {
    mutateAsync: deleteEventsMutation,
    isLoading: isLoadingDeleteEvents
  } = useMutation<DeleteEventsResponse, unknown, DeleteEventsPayload, unknown>(
    [INSTANCES_DELETE_QUERY_KEY],
    deleteEventsFunction,
    {
      onSuccess: () => {
        queryClient.invalidateQueries([REVISIONS_QUERY_KEY])
      }
    }
  )

  const handleDeleteEvents = async (values: DeleteEventsPayload) => {
    try {
      const result = await deleteEventsMutation(values)
      return result
    } catch (err) {
      return err
    }
  }

  return {
    deleteEvents: handleDeleteEvents,
    isLoadingDeleteEvents
  }
}

export default useInstancesDelete
